import React from 'react';
import { parseRawDateValue, RawDateValue } from '../../../utils/date-value';
import { Controller, useForm } from 'react-hook-form';
import isDate from 'validator/lib/isDate';
import { getClientReconciliationActUrl } from '../../../utils/common.utils';
import { InputWrapper } from '../../common/input-wrapper';
import { Button, CustomProvider, DateRangePicker } from 'rsuite';
import { ruRU } from 'rsuite/locales';
import { ModalProps } from '../../../services/modals.service';
import { User, UserSelect } from '../../common/user-select';
import { Select } from '../../common/select.component';
import { useRequired } from '../../../utils/validations';
import { useModalClose } from '../../../utils/useModalClose';
import { Period, useReportLink } from './useReportLink';
import { FormattedMessage, useIntl } from 'react-intl';
import moment from 'moment';

type Form = {
  client?: User | null;
  currency?: 'cny' | 'usd';
  period: Period;
  range?: RawDateValue[];
};

export function isCorrectDate(v: string) {
  return isDate(`${v}`, {
    format: 'DD.MM.YYYY',
    delimiters: ['.'],
    strictMode: true,
  });
}

export const ClientReconciliationActModal: React.FC<ModalProps> = ({
  close,
}) => {
  const intl = useIntl();
  const required = useRequired();

  const periods: Array<{ title: string; id: Period }> = [
    {
      title: intl.formatMessage({
        defaultMessage: 'За все время',
        id: 'ClientReconciliationActModal.title.AllTime',
        description: 'Период',
      }),
      id: 'all_time',
    },
    {
      title: intl.formatMessage({
        defaultMessage: 'Текущая неделя',
        id: 'ClientReconciliationActModal.title.Week',
        description: 'Период',
      }),
      id: 'week',
    },
    {
      title: intl.formatMessage({
        defaultMessage: 'Текущий месяц',
        id: 'ClientReconciliationActModal.title.Month',
        description: 'Период',
      }),
      id: 'month',
    },
    {
      title: intl.formatMessage({
        defaultMessage: 'Этот год',
        id: 'ClientReconciliationActModal.title.Year',
        description: 'Период',
      }),
      id: 'year',
    },
    {
      title: intl.formatMessage({
        defaultMessage: 'Прошлая неделя',
        id: 'ClientReconciliationActModal.title.LastWeek',
        description: 'Период',
      }),
      id: 'last_week',
    },
    {
      title: intl.formatMessage({
        defaultMessage: 'Прошлый месяц',
        id: 'ClientReconciliationActModal.title.LastMonth',
        description: 'Период',
      }),
      id: 'last_month',
    },
    {
      title: intl.formatMessage({
        defaultMessage: 'Прошлый год',
        id: 'ClientReconciliationActModal.title.LastYear',
        description: 'Период',
      }),
      id: 'last_year',
    },
    {
      title: intl.formatMessage({
        defaultMessage: 'Выбрать диапазон',
        id: 'ClientReconciliationActModal.title.SelectRange',
        description: 'Период',
      }),
      id: 'range',
    },
  ];

  const {
    control,
    formState: { isValid },
    watch,
    trigger,
  } = useForm<Form>({
    mode: 'all',
    defaultValues: {
      period: 'all_time',
      currency: 'cny',
    },
  });

  const [client, currency, period, range] = watch([
    'client',
    'currency',
    'period',
    'range',
  ]);

  React.useEffect(() => {
    trigger(['range']);
  }, [trigger, range]);

  const params = {
    clientId: client?.internalId,
    currency,
  };

  const reportLink = useReportLink(
    isValid,
    period,
    params as any,
    getClientReconciliationActUrl as any,
    range?.[0],
    range?.[1],
  );

  const modalRef = useModalClose(close);

  return (
    <CustomProvider locale={ruRU}>
      <div className="modal-dialog" ref={modalRef}>
        <form className="modal-content">
          <div className="modal-body">
            <div className="d-flex flex-row justify-content-between align-items-center">
              <h3>
                <FormattedMessage
                  defaultMessage="Акт сверки с клиентом"
                  id="ClientReconciliationActModal.title.act"
                  description="Заголовок"
                />
              </h3>
              <div className="btn p-0" onClick={close}>
                <i className="bi bi-x fs-36 color-gray-600" />
              </div>
            </div>
            <Controller
              control={control}
              name="client"
              rules={required}
              render={({ field, fieldState }) => (
                <InputWrapper
                  className="mb-3"
                  title={intl.formatMessage({
                    defaultMessage: 'Клиент',
                    id: 'ClientReconciliationActModal.title.Client',
                    description: 'Пояснение к полю ввода',
                  })}
                  required
                  error={fieldState.error?.message}
                >
                  <UserSelect
                    placeholder={intl.formatMessage({
                      defaultMessage: 'Выберите клиента',
                      id: 'ClientReconciliationActModal.placeholder.SelectClient',
                      description: 'Пояснение к полю ввода',
                    })}
                    {...field}
                    value={field.value || null}
                    roles={['client']}
                  />
                </InputWrapper>
              )}
            />
            <Controller
              name="currency"
              control={control}
              rules={required}
              render={({ field }) => (
                <Select
                  className="mb-3"
                  title={intl.formatMessage({
                    defaultMessage: 'Валюта',
                    id: 'ClientReconciliationActModal.title.Currency',
                    description: 'Пояснение к полю ввода',
                  })}
                  placeholder={intl.formatMessage({
                    defaultMessage: 'Выберите валюту',
                    id: 'ClientReconciliationActModal.placeholder.SelectCurrency',
                    description: 'Пояснение к полю ввода',
                  })}
                  required
                  options={[
                    {
                      value: 'cny',
                      label: intl.formatMessage({
                        defaultMessage: 'Юань',
                        id: 'ClientReconciliationActModal.title.Yuan',
                        description: 'Валюта',
                      }),
                    },
                    {
                      value: 'usd',
                      label: intl.formatMessage({
                        defaultMessage: 'Доллар',
                        id: 'ClientReconciliationActModal.title.Dollar',
                        description: 'Валюта',
                      }),
                    },
                  ]}
                  {...field}
                />
              )}
            />
            <Controller
              name="period"
              control={control}
              rules={{
                required: intl.formatMessage({
                  defaultMessage: 'Укажите период',
                  id: 'ClientReconciliationActModal.title.SpecifyPeriod',
                  description: 'Пояснение к полю ввода',
                }),
              }}
              render={({ field, fieldState }) => {
                return (
                  <InputWrapper
                    required
                    title={intl.formatMessage({
                      defaultMessage: 'Выберите период',
                      id: 'ClientReconciliationActModal.title.SelectPeriod',
                      description: 'Пояснение к полю ввода',
                    })}
                    error={fieldState.error?.message}
                    className="mb-3"
                  >
                    <select className="form-select" {...field}>
                      {periods.map((item) => {
                        return (
                          <option
                            value={item.id}
                            key={item.id}
                            className="mb-1 cursor-pointer bg-white py-3 pl-3 pr-7 text-base-lh-125 font-medium text-gray-800 hover:bg-green-100"
                          >
                            {item.title}
                          </option>
                        );
                      })}
                    </select>
                  </InputWrapper>
                );
              }}
            />
            {period === 'range' && (
              <Controller
                name="range"
                control={control}
                rules={{
                  validate: (value) => {
                    if (!value || value.length !== 2) return true;

                    const [start, end] = value;

                    if (!isCorrectDate(start) || !isCorrectDate(end)) {
                      return 'Укажите корректные даты';
                    }

                    if (
                      parseRawDateValue(start).isAfter(parseRawDateValue(end))
                    ) {
                      return 'Дата начала не должна быть позже даты окончания';
                    }

                    return true;
                  },
                }}
                render={({ field, fieldState }) => {
                  return (
                    <InputWrapper
                      title={intl.formatMessage({
                        defaultMessage: 'Выберите период',
                        id: 'ClientReconciliationActModal.title.SelectPeriodRange',
                        description: 'Пояснение к полю ввода',
                      })}
                      error={fieldState.error?.message}
                      className="mb-2 w-full text-base-lh-100"
                    >
                      <DateRangePicker
                        className={'w-100'}
                        format={'dd.MM.yyyy'}
                        isoWeek
                        showOneCalendar
                        onChange={(range) => {
                          if (range == null) field.onChange([]);
                          else {
                            field.onChange(
                              range.map((date) =>
                                moment(date).format('DD.MM.YYYY'),
                              ),
                            );
                          }
                        }}
                      />
                    </InputWrapper>
                  );
                }}
              />
            )}
          </div>
          <div className="modal-footer justify-content-end">
            {reportLink ? (
              <Button
                as="a"
                href={reportLink}
                target="_blank"
                className="btn btn-success"
              >
                <FormattedMessage
                  defaultMessage="Скачать в Excel"
                  id="ClientReconciliationActModal.button.DownloadinExcel"
                  description="Кнопка"
                />
              </Button>
            ) : (
              <Button as="button" disabled={true} className="btn btn-success">
                <FormattedMessage
                  defaultMessage="Скачать в Excel"
                  id="ClientReconciliationActModal.button.DownloadinExcel"
                  description="Кнопка"
                />
              </Button>
            )}
          </div>
        </form>
      </div>
    </CustomProvider>
  );
};
